import { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  getAxisOptions,
  getChartConfig,
  getDrilldownConfig,
  getGroupByMapping,
} from '../api/constants';
import { fetchDataAccessLists, fetchModuleAccessLists } from '../api/permissions';
import { ReactComponent as AbsenceMetricsIcon } from '../assets/icons/AbsenceMetricsIcon.svg';
import { ReactComponent as AllMetricsIcon } from '../assets/icons/AllMetricsIcon.svg';
import { ReactComponent as ContractStatusIcon } from '../assets/icons/ContractStatusIcon.svg';
import { ReactComponent as DateIcon } from '../assets/icons/DateIcon.svg';
import { ReactComponent as EntitiesIcon } from '../assets/icons/EntitiesIcon.svg';
import { ReactComponent as HeadcountMetricsIcon } from '../assets/icons/HeadcountMetricsIcon.svg';
import { ReactComponent as LearningMetricsIcon } from '../assets/icons/LearningMetricsIcon.svg';
import { ReactComponent as RecruitmentMetricsIcon } from '../assets/icons/RecruitmentMetricsIcon.svg';
import { injectIcons } from '../utils/format';

export const useDataAccess = () => {
  const { data, isLoading, error, refetch } = useQuery(['userDomains'], () =>
    fetchDataAccessLists()
  );

  return {
    dataAccessLists: data?.data || [],
    isLoading,
    error,
    refetch,
  };
};

export const useAxisOptions = ({ metricValues = {}, chartType }) => {
  const { value: metric, subValue: subMetric } = metricValues;
  const { data, isLoading, error, refetch } = useQuery(['xAxisOptions', subMetric, chartType], () =>
    getAxisOptions({ metric, subMetric, chartType })
  );

  const iconMap = {
    All: AllMetricsIcon,
    DateIcon,
    EntitiesIcon,
    HeadcountMetricsIcon,
    ContractStatusIcon,
    AbsenceMetricsIcon,
    LearningMetricsIcon,
    RecruitmentMetricsIcon,
  };

  const xAxisOptions = useMemo(() => {
    return data ? injectIcons(data, iconMap) : [];
  }, [data]);
  return {
    xAxisOptions,
    isLoading,
    error,
    refetch,
  };
};

export const useModuleAccess = () => {
  const { data, isLoading, error, refetch } = useQuery(['moduleAccess'], () =>
    fetchModuleAccessLists()
  );

  const moduleLists = data?.moduleAccess || {};

  const uniqueModuleValues = [...new Set(Object.values(moduleLists).flat())];
  return {
    moduleList: uniqueModuleValues || [],
    moduleListByCategories: data?.moduleAccess,
    isLoading,
    error,
    refetch,
  };
};

export default useDataAccess;

export const useChartConfig = () => {
  const { data, isLoading, error, refetch } = useQuery(['chartConfig'], () => getChartConfig());

  return {
    data: data || [],
    isLoading,
    error,
    refetch,
  };
};

export const useGroupByMapping = () => {
  const { data, isLoading, error, refetch } = useQuery(['groupby-mapping'], () =>
    getGroupByMapping()
  );

  return {
    groupByMapping: data || {},
    isLoading,
    error,
    refetch,
  };
};

export const useDrilldownConfig = () => {
  const { data, isLoading, error, refetch } = useQuery(['drilldown-config'], () =>
    getDrilldownConfig()
  );

  return {
    drilldownConfig: data?.drilldownConfig || {},
    isLoading,
    error,
    refetch,
  };
};
